import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import Toast from "../components/ui/Toast";

const ToastContext = createContext(null);

let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    ({ title, message, variant = "success", duration = 4000 }) => {
      const id = ++idCounter;
      setToasts((prev) => [...prev, { id, title, message, variant }]);
      if (duration > 0) {
        timersRef.current[id] = setTimeout(() => removeToast(id), duration);
      }
      return id;
    },
    [removeToast],
  );

  const toast = useCallback(
    (messageOrOpts) => {
      if (typeof messageOrOpts === "string") {
        return addToast({ message: messageOrOpts });
      }
      return addToast(messageOrOpts);
    },
    [addToast],
  );

  toast.success = (message, opts) =>
    addToast({ variant: "success", message, ...opts });
  toast.error = (message, opts) =>
    addToast({ variant: "error", message, duration: 6000, ...opts });
  toast.info = (message, opts) =>
    addToast({ variant: "info", message, ...opts });
  toast.warning = (message, opts) =>
    addToast({ variant: "warning", message, ...opts });

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
